// =======================
// sear/src/paginas/paginaEscola/perfil/infoPerfilMant.js
// =======================

import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Modal,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { motion } from "framer-motion";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ListaAtos from "./atosMant";
import { api } from "@api/api";

const formatTelefone = (telefone) => {
  if (!telefone) return "";

  const cleaned = telefone.replace(/\D/g, "");

  if (cleaned.length === 11) {
    return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  }

  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
  }

  return telefone;
};

const formatCEP = (cep) => {
  if (!cep) return "";

  return cep.replace(/(\d{5})(\d{3})/, "$1-$2");
};

const normalizarTexto = (valor = "") =>
  String(valor)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const mascararCNPJ = (cnpj) => {
  if (!cnpj) return "Não informado";

  return "**.***.***/****-**";
};

function ModalAtosMantenedora({ open, onClose }) {
  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        onClick={onClose}
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 2,
        }}
      >
        <motion.div
          onClick={(event) => event.stopPropagation()}
          initial={{
            opacity: 0,
            scale: 0.95,
          }}
          animate={{
            opacity: 1,
            scale: 1,
          }}
          transition={{
            duration: 0.2,
          }}
          style={{
            width: "100%",
            maxWidth: 900,
          }}
        >
          <Paper
            sx={{
              position: "relative",
              maxHeight: "90vh",
              overflowY: "auto",
              padding: {
                xs: 2,
                md: 3,
              },
              borderRadius: 2,
              outline: "none",
            }}
          >
            <IconButton
              onClick={onClose}
              aria-label="Fechar"
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                color: "#5f6f94",
                zIndex: 1,

                "&:hover": {
                  color: "#0767d8",
                  backgroundColor: "#eaf4ff",
                },
              }}
            >
              <CloseRoundedIcon />
            </IconButton>

            <ListaAtos />
          </Paper>
        </motion.div>
      </Box>
    </Modal>
  );
}

function ModalMantenedora({ open, onClose, mantenedora, onOpenAtos }) {
  if (!mantenedora) return null;

  const escolas = Array.isArray(mantenedora.escolas) ? mantenedora.escolas : [];

  const etapasModalidades = Array.isArray(mantenedora.etapasModalidades)
    ? mantenedora.etapasModalidades
    : [];

  const endereco =
    [
      [mantenedora.rua, mantenedora.numero]
        .filter(
          (valor) => valor !== null && valor !== undefined && valor !== "",
        )
        .join(", "),
      mantenedora.bairro,
    ]
      .filter(Boolean)
      .join(" — ") || "Não informado";

  const municipioUF =
    [mantenedora.cidade, mantenedora.estado].filter(Boolean).join(" — ") ||
    "Não informado";

  const campos = [
    ["CNPJ", mascararCNPJ(mantenedora.cnpj)],
    ["Endereço", endereco],
    ["Município/UF", municipioUF],
    ["CEP", formatCEP(mantenedora.cep) || "Não informado"],
    ["Telefone", formatTelefone(mantenedora.telefone) || "Não informado"],
    ["E-mail", mantenedora.email || "Não informado"],
  ];

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        onClick={onClose}
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 2,
        }}
      >
        <motion.div
          onClick={(event) => event.stopPropagation()}
          initial={{
            opacity: 0,
            scale: 0.95,
          }}
          animate={{
            opacity: 1,
            scale: 1,
          }}
          transition={{
            duration: 0.2,
          }}
          style={{
            width: "100%",
            maxWidth: 590,
          }}
        >
          <Paper
            sx={{
              position: "relative",
              maxHeight: "90vh",
              overflowY: "auto",
              padding: {
                xs: 2,
                md: 3,
              },
              borderRadius: 2,
              outline: "none",
            }}
          >
            <IconButton
              onClick={onClose}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                color: "#3156a4",
              }}
            >
              <CloseRoundedIcon />
            </IconButton>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                paddingRight: 5,
              }}
            >
              <Avatar
                sx={{
                  width: 54,
                  height: 54,
                  color: "#087cf0",
                  backgroundColor: "#eaf4ff",
                }}
              >
                <AccountBalanceRoundedIcon />
              </Avatar>

              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#0767d8",
                    fontWeight: 700,
                  }}
                >
                  {mantenedora.nome}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "#5f6f94",
                  }}
                >
                  Dados institucionais e vínculos
                </Typography>
              </Box>
            </Box>

            <Divider
              sx={{
                marginY: 2,
                borderColor: "#c9def4",
              }}
            />

            <Typography
              sx={{
                color: "#0767d8",
                fontWeight: 700,
                marginBottom: 1.5,
              }}
            >
              Dados da Mantenedora
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                },
                gap: 2,
              }}
            >
              {campos.map(([rotulo, valor]) => (
                <Box key={rotulo}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#5f6f94",
                    }}
                  >
                    {rotulo}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "#33446b",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {valor}
                  </Typography>
                </Box>
              ))}
            </Box>

            {etapasModalidades.length > 0 && (
              <>
                <Divider
                  sx={{
                    marginY: 2,
                    borderColor: "#c9def4",
                  }}
                />

                <Typography
                  sx={{
                    color: "#0767d8",
                    fontWeight: 700,
                    marginBottom: 1,
                  }}
                >
                  Etapas e modalidades mantidas
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  {etapasModalidades.map((item) => {
                    const nome = typeof item === "string" ? item : item.nome;

                    return (
                      <Chip
                        key={nome}
                        label={nome}
                        size="small"
                        variant="outlined"
                        sx={{
                          color: "#0767d8",
                          borderColor: "#087cf0",
                        }}
                      />
                    );
                  })}
                </Box>
              </>
            )}

            <Divider
              sx={{
                marginY: 2,
                borderColor: "#c9def4",
              }}
            />

            <Typography
              sx={{
                color: "#0767d8",
                fontWeight: 700,
                marginBottom: 1,
              }}
            >
              Escolas vinculadas
            </Typography>

            <Box
              sx={{
                display: "grid",
                gap: 1,
              }}
            >
              {escolas.map((escola) => {
                const modalidades = Array.isArray(escola.modalidades)
                  ? escola.modalidades
                  : escola.modalidade
                    ? [escola.modalidade]
                    : [];

                return (
                  <Paper
                    key={escola.id_procura_atos || escola.id}
                    variant="outlined"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      padding: 1.25,
                      borderColor: "#c9def4",
                      boxShadow: "none",
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 38,
                        height: 38,
                        color: "#087cf0",
                        backgroundColor: "#eaf4ff",
                      }}
                    >
                      <SchoolRoundedIcon fontSize="small" />
                    </Avatar>

                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#3156a4",
                          fontWeight: 700,
                        }}
                      >
                        {escola.nome}
                      </Typography>

                      {modalidades.length > 0 && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#5f6f94",
                          }}
                        >
                          {modalidades.join(", ")}
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                );
              })}

              {escolas.length === 0 && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "#5f6f94",
                  }}
                >
                  Nenhuma escola vinculada encontrada.
                </Typography>
              )}
            </Box>

            <Divider
              sx={{
                marginY: 2,
                borderColor: "#c9def4",
              }}
            />

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Button
                variant="contained"
                startIcon={<DescriptionOutlinedIcon />}
                onClick={onOpenAtos}
                sx={{
                  minWidth: 110,
                  textTransform: "none",
                  backgroundColor: "#087cf0",
                  boxShadow: "none",
                }}
              >
                Ver atos
              </Button>
            </Box>
          </Paper>
        </motion.div>
      </Box>
    </Modal>
  );
}

export default function MantenedoraComponent() {
  const [mantenedoras, setMantenedoras] = useState([]);
  const [busca, setBusca] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalDetalhesOpen, setModalDetalhesOpen] = useState(false);
  const [modalAtosOpen, setModalAtosOpen] = useState(false);
  const [mantenedoraSelecionada, setMantenedoraSelecionada] = useState(null);

  useEffect(() => {
    const carregarDados = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem("token");

        const headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        };

        const [respostaMantenedora, respostaEscolas] = await Promise.all([
          fetch(api.maintainerData(), {
            credentials: "include",
            headers,
          }),

          fetch(api.listUserSchools(), {
            credentials: "include",
            headers,
          }),
        ]);

        if (!respostaMantenedora.ok) {
          const erro = await respostaMantenedora.json();

          throw new Error(
            erro.message || `Erro: ${respostaMantenedora.status}`,
          );
        }

        const dadosMantenedora = await respostaMantenedora.json();

        const dadosEscolas = respostaEscolas.ok
          ? await respostaEscolas.json()
          : { escolas: [] };

        const escolasRecebidas = Array.isArray(dadosEscolas?.escolas)
          ? dadosEscolas.escolas
          : [];

        const escolasAgrupadas = Object.values(
          escolasRecebidas.reduce((acc, escola) => {
            const chave = escola.id_procura_atos || escola.id;

            const modalidadesRecebidas = Array.isArray(escola.modalidades)
              ? escola.modalidades
              : escola.modalidade
                ? [escola.modalidade]
                : [];

            if (!acc[chave]) {
              acc[chave] = {
                ...escola,
                modalidades: [...modalidadesRecebidas],
              };

              return acc;
            }

            modalidadesRecebidas.forEach((modalidade) => {
              if (!acc[chave].modalidades.includes(modalidade)) {
                acc[chave].modalidades.push(modalidade);
              }
            });

            return acc;
          }, {}),
        );

        const listaMantenedoras = Array.isArray(dadosMantenedora)
          ? dadosMantenedora
          : Array.isArray(dadosMantenedora?.mantenedoras)
            ? dadosMantenedora.mantenedoras
            : dadosMantenedora
              ? [dadosMantenedora]
              : [];

        const mantenedorasNormalizadas = listaMantenedoras.map(
          (mantenedora) => ({
            ...mantenedora,

            etapasModalidades: Array.isArray(mantenedora.etapasModalidades)
              ? mantenedora.etapasModalidades
              : [],

            escolas: Array.isArray(mantenedora.escolas)
              ? mantenedora.escolas
              : listaMantenedoras.length === 1
                ? escolasAgrupadas
                : [],
          }),
        );

        setMantenedoras(mantenedorasNormalizadas);
      } catch (error) {
        console.error("Erro ao carregar os dados institucionais:", error);

        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    carregarDados();
  }, []);

  const handleOpenDetalhes = (mantenedora) => {
    setMantenedoraSelecionada(mantenedora);
    setModalDetalhesOpen(true);
  };

  const handleOpenAtos = (mantenedora) => {
    setMantenedoraSelecionada(mantenedora);
    setModalAtosOpen(true);
  };

  const mantenedorasFiltradas = mantenedoras.filter((mantenedora) => {
    const termo = normalizarTexto(busca);

    return (
      normalizarTexto(mantenedora.nome).includes(termo) ||
      normalizarTexto(mantenedora.cidade).includes(termo) ||
      normalizarTexto(mantenedora.cnpj).includes(termo)
    );
  });

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error">Erro: {error}</Typography>;
  }

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          padding: {
            xs: 2,
            md: 3,
          },
          border: "1px solid #9fcdff",
          borderRadius: 2,
          backgroundColor: "#ffffff",
          boxShadow: "none",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: {
              xs: "stretch",
              md: "center",
            },
            justifyContent: "space-between",
            flexDirection: {
              xs: "column",
              md: "row",
            },
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{
                color: "#0767d8",
                fontWeight: 700,
              }}
            >
              Mantenedoras vinculadas
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: "#5f6f94",
                marginTop: 0.5,
              }}
            >
              {/* Consulte as mantenedoras responsáveis pelas escolas vinculadas.*/}
            </Typography>
          </Box>

          <TextField
            value={busca}
            onChange={(event) => setBusca(event.target.value)}
            placeholder="Pesquisar mantenedora..."
            size="small"
            sx={{
              width: {
                xs: "100%",
                md: 325,
              },

              "& .MuiOutlinedInput-root": {
                minHeight: 48,
                borderRadius: 2,

                "& fieldset": {
                  borderColor: "#9fcdff",
                },

                "&:hover fieldset": {
                  borderColor: "#087cf0",
                },

                "&.Mui-focused fieldset": {
                  borderColor: "#087cf0",
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon
                    sx={{
                      color: "#5f6f94",
                    }}
                  />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gap: 2,
            marginTop: 3,
          }}
        >
          {mantenedorasFiltradas.map((mantenedora) => {
            const quantidadeEscolas = mantenedora.escolas.length;

            return (
              <Paper
                key={mantenedora.id}
                variant="outlined"
                sx={{
                  padding: 2.5,
                  border: "1px solid #9fcdff",
                  borderRadius: 2,
                  boxShadow: "none",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: {
                      xs: "flex-start",
                      md: "center",
                    },
                    flexDirection: {
                      xs: "column",
                      md: "row",
                    },
                    gap: 2,
                  }}
                >
                  <Avatar
                    sx={{
                      width: 64,
                      height: 64,
                      color: "#087cf0",
                      backgroundColor: "#eaf4ff",
                    }}
                  >
                    <AccountBalanceRoundedIcon fontSize="large" />
                  </Avatar>

                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      display: {
                        xs: "none",
                        md: "block",
                      },
                      borderColor: "#c9def4",
                    }}
                  />

                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#0767d8",
                        fontWeight: 700,
                      }}
                    >
                      {mantenedora.nome}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2,
                        color: "#4f5f85",
                        marginTop: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <LocationOnOutlinedIcon fontSize="small" />

                        <Typography variant="body2">
                          {mantenedora.cidade}
                        </Typography>
                      </Box>

                      <Typography variant="body2">
                        CNPJ: {mascararCNPJ(mantenedora.cnpj)}
                      </Typography>
                    </Box>

                    {mantenedora.etapasModalidades.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                          marginTop: 1.5,
                        }}
                      >
                        {mantenedora.etapasModalidades.map((item) => {
                          const nome =
                            typeof item === "string" ? item : item.nome;

                          return (
                            <Chip
                              key={nome}
                              label={nome}
                              size="small"
                              variant="outlined"
                              sx={{
                                color: "#0767d8",
                                borderColor: "#087cf0",
                                backgroundColor: "#ffffff",
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        color: "#4f5f85",
                        marginTop: 1.25,
                      }}
                    >
                      <SchoolRoundedIcon fontSize="small" />

                      <Typography variant="body2">
                        {quantidadeEscolas}{" "}
                        {quantidadeEscolas === 1
                          ? "escola vinculada"
                          : "escolas vinculadas"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                      justifyContent: {
                        xs: "flex-start",
                        md: "flex-end",
                      },
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => handleOpenDetalhes(mantenedora)}
                      sx={{
                        minWidth: 142,
                        textTransform: "none",
                        borderColor: "#087cf0",
                        color: "#0767d8",
                      }}
                    >
                      Ver detalhes
                    </Button>

                    <Button
                      variant="contained"
                      startIcon={<DescriptionOutlinedIcon />}
                      onClick={() => handleOpenAtos(mantenedora)}
                      sx={{
                        minWidth: 142,
                        textTransform: "none",
                        backgroundColor: "#087cf0",
                        boxShadow: "none",
                      }}
                    >
                      Ver atos
                    </Button>
                  </Box>
                </Box>
              </Paper>
            );
          })}

          {mantenedorasFiltradas.length === 0 && (
            <Typography
              sx={{
                color: "#5f6f94",
                textAlign: "center",
                padding: 3,
              }}
            >
              Nenhuma mantenedora encontrada.
            </Typography>
          )}
        </Box>
      </Paper>

      <ModalMantenedora
        open={modalDetalhesOpen}
        onClose={() => setModalDetalhesOpen(false)}
        mantenedora={mantenedoraSelecionada}
        onOpenAtos={() => {
          setModalDetalhesOpen(false);
          setModalAtosOpen(true);
        }}
      />

      <ModalAtosMantenedora
        open={modalAtosOpen}
        onClose={() => setModalAtosOpen(false)}
      />
    </>
  );
}
